import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Wallet, FileText, ShieldCheck, BarChart3, Bell, Settings as SettingsIcon,
  BookOpen, Calculator, Menu, X, LogOut, Bell as BellIcon, ClipboardList, Mic,
  TrendingUp, GitCompareArrows, Wand2, Users, XCircle, CalendarCheck, Clock, MessageSquare, Bookmark, Sparkles, Newspaper, Gavel, Radar,
} from "lucide-react";
import { Logo } from "./Logo";
import { LanguagePill } from "./LanguagePill";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function AppLayout({ children }) {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const nav = [
    { to: "/dashboard", icon: LayoutDashboard, label: t("dashboard") },
    { to: "/updates", icon: Newspaper, label: t("nav_updates") },
    { to: "/conversations", icon: MessageSquare, label: t("nav_conversations") },
    { to: "/wallet", icon: Wallet, label: t("my_wallet") },
    { to: "/documents", icon: FileText, label: t("documents") },
    { to: "/premium-change", icon: TrendingUp, label: t("nav_premium_change") },
    { to: "/compare", icon: GitCompareArrows, label: t("nav_compare") },
    { to: "/simulator", icon: Wand2, label: t("nav_simulator") },
    { to: "/saved", icon: Bookmark, label: t("nav_saved") },
    { to: "/life-events", icon: Sparkles, label: t("nav_life_events") },
    { to: "/claims", icon: ClipboardList, label: t("claims") },
    { to: "/denial-fighter", icon: Gavel, label: t("nav_denial") },
    { to: "/gap-radar", icon: Radar, label: t("nav_gap_radar") },
    { to: "/cancellation", icon: XCircle, label: t("nav_cancellation") },
    { to: "/missed-payment", icon: Clock, label: t("nav_missed_payment") },
    { to: "/protection", icon: ShieldCheck, label: t("protection_score") },
    { to: "/checkup", icon: CalendarCheck, label: t("nav_checkup") },
    { to: "/budget", icon: BarChart3, label: t("budget_builder") },
    { to: "/household", icon: Users, label: t("nav_household") },
    { to: "/glossary", icon: BookOpen, label: t("glossary") },
    { to: "/calculators", icon: Calculator, label: t("calculators") },
    { to: "/reminders", icon: Bell, label: t("reminders") },
    { to: "/settings", icon: SettingsIcon, label: t("settings") },
  ];

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const openVoice = () => {
    if (typeof window !== "undefined" && window.self !== window.top) {
      window.open(`${window.location.origin}/voice`, "_blank", "noopener");
    } else {
      navigate("/voice");
    }
  };

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      <div className="px-5 py-5"><Logo /></div>
      <nav className="flex-1 space-y-1 px-3 overflow-y-auto">
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={`sidebar-${n.to.slice(1)}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "bg-blue-50 text-brandblue" : "text-slate-600 hover:bg-slate-50"
              }`
            }>
            <n.icon className="h-[18px] w-[18px]" />
            <span className="truncate">{n.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="m-3 rounded-2xl bg-teal-50/70 border border-teal-100 p-4">
        <div className="flex items-center gap-2 text-brandteal"><ShieldCheck className="h-4 w-4" />
          <span className="text-sm font-semibold">{t("sidebar_independent")}</span></div>
        <p className="mt-1 text-xs text-slate-500">{t("sidebar_work_for_you")}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-slate-100 bg-white lg:block">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 bg-white shadow-xl">
            <button onClick={() => setOpen(false)} className="absolute right-3 top-3 text-slate-400"><X className="h-5 w-5" /></button>
            <SidebarContent />
          </div>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-100 bg-white px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-slate-600" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="h-6 w-6" />
            </button>
            <div className="lg:hidden"><Logo size="sm" /></div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="voice-launch-btn" onClick={openVoice}
              className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-2 text-sm font-medium text-brandblue hover:bg-blue-100 transition-colors">
              <Mic className="h-4 w-4" /> <span className="hidden sm:inline">{t("talk_ai")}</span>
            </button>
            <button data-testid="notifications-btn" onClick={() => navigate("/reminders")}
              className="relative rounded-full p-2 text-slate-500 hover:bg-slate-100">
              <BellIcon className="h-5 w-5" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-btn" className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 hover:bg-slate-50">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full brand-gradient text-sm font-semibold text-white">{initials}</span>
                  <span className="hidden sm:block text-sm font-medium text-navy">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate("/settings")}>{t("settings")}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-btn" onClick={async () => { await logout(); navigate("/"); }} className="text-red-600">
                  <LogOut className="mr-2 h-4 w-4" /> {t("sign_out")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="hidden sm:block"><LanguagePill /></div>
          </div>
        </header>
        <main className="mx-auto max-w-7xl px-4 sm:px-6 py-6">{children}</main>
      </div>

      {/* Floating Talk-to-AI button (voice from anywhere) */}
      <button data-testid="voice-fab" onClick={openVoice}
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full brand-gradient text-white card-shadow-lg hover:scale-105 transition-transform"
        aria-label={t("talk_ai")}>
        <Mic className="h-6 w-6" />
      </button>
    </div>
  );
}
